import { useState, useRef, useEffect } from 'react';
import type React from 'react';
import Input from 'storybookConfig/stories/molecules/Input';
import styled from '@emotion/styled';
import { ThemeColors } from 'storybookConfig/stories/theme';

export const GradientSlider = styled(Input)`
  appearance: none;
  -webkit-appearance: none;
  margin-bottom: 8px;
  width: 100%;
  height: 6px;
  background: none; /* remove native background */
  cursor: pointer;
  border: none;

  &::-webkit-slider-runnable-track {
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(
      to right,
      ${ThemeColors.errorBase} 0%,
      ${ThemeColors.errorBase} 8%,
      ${ThemeColors.warningBase} 12%,
      ${ThemeColors.warningBase} 13%,
      ${ThemeColors.successBase} 17%,
      ${ThemeColors.successBase} 18%,
      ${ThemeColors.accent} 22%,
      ${ThemeColors.accent} 78%,
      ${ThemeColors.successBase} 82%,
      ${ThemeColors.successBase} 83%,
      ${ThemeColors.warningBase} 87%,
      ${ThemeColors.warningBase} 88%,
      ${ThemeColors.errorBase} 92%,
      ${ThemeColors.errorBase} 100%
    );
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.surfaceCanvas};
    border: 2px solid ${ThemeColors.accent};
    margin-top: -5px;
  }

  &::-moz-range-track {
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(
      to right,
      ${ThemeColors.errorBase} 0%,
      ${ThemeColors.errorBase} 8%,
      ${ThemeColors.warningBase} 12%,
      ${ThemeColors.warningBase} 13%,
      ${ThemeColors.successBase} 17%,
      ${ThemeColors.successBase} 18%,
      ${ThemeColors.accent} 22%,
      ${ThemeColors.accent} 78%,
      ${ThemeColors.successBase} 82%,
      ${ThemeColors.successBase} 83%,
      ${ThemeColors.warningBase} 87%,
      ${ThemeColors.warningBase} 88%,
      ${ThemeColors.errorBase} 92%,
      ${ThemeColors.errorBase} 100%
    );
  }

  &::-moz-range-thumb {
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.surfaceCanvas};
    border: 2px solid ${ThemeColors.accent};
  }
`;

type RevenueSliderStandaloneProps = {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  onChange?: (value: number) => void;
  label?: (value: number) => React.ReactNode;
  width?: number | string;
  onFocus?: () => void;
};

const RevenueSliderStandalone: React.FC<RevenueSliderStandaloneProps> = ({
  min = 0,
  max = 100,
  step = 1,
  value: controlledValue,
  onChange: onControlledChange,
  label = (val) => (
    <div style={{ textAlign: 'center' }}>
      You keep:
      <br />
      <span style={{ fontWeight: 'bold' }}>{val}%</span>
    </div>
  ),
  width = 400,
  onFocus,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(min);
  const value = isControlled ? controlledValue : internalValue;

  const sliderRef = useRef<HTMLInputElement>(null);
  const [thumbPosition, setThumbPosition] = useState(0);

  const updateThumbPosition = (val: number) => {
    if (!sliderRef.current) return;

    const range = sliderRef.current;
    const percent = (val - min) / (max - min);
    const thumbWidth = 20; // Estimate slider thumb size

    const offset = percent * (range.offsetWidth - thumbWidth);
    setThumbPosition(offset);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (!isControlled) setInternalValue(newValue);
    if (onControlledChange) onControlledChange(newValue);
    updateThumbPosition(newValue);
  };

  useEffect(() => {
    updateThumbPosition(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        position: 'relative',
        width,
        padding: '12px 0',
      }}
    >
      {/* Tooltip above slider thumb */}
      <div
        style={{
          position: 'absolute',
          left: thumbPosition,
          top: 0,
          transform: 'translate(-30%, -40%)',
          color: ThemeColors.bodyTextPrimary,
          padding: '2px 2px',
          borderRadius: '4px',
          fontSize: '14px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        {label(value)}
      </div>

      {/* Range input */}
      <GradientSlider
        type="range"
        ref={sliderRef}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleSliderChange}
        onFocus={onFocus}
        style={{ width: '100%', justifyContent: 'center' }}
      />
    </div>
  );
};

export default RevenueSliderStandalone;
